import moment from 'moment';
import { Alert, Badge, Toggle } from '@odin-labs/components';
import { AuthContext } from 'auth';
import {
  JobsiteWorker,
  JobsiteWorkerDocumentType,
  JwDocument,
} from 'containers/workerOnboarding/types';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { cn, getUserFullName, useBoolean } from 'utils';
import { getPrettyFormattedDate } from 'utils/dates';

type VideoAcknowledgmentStatusProps = Partial<{
  name: string;
  value: boolean;
  onChange: (checked: boolean) => void;
  error: React.ReactNode;
  loading: boolean;
  jobsiteWorker: JobsiteWorker;
  documentType: JobsiteWorkerDocumentType;
  isVideoRequired: boolean;
  acknowledgedBy: JwDocument['acknowledgedBy'];
}>;

const classes = {
  container: (hasError: boolean): string =>
    cn(
      'odin-flex odin-flex-col odin-gap-y-2',
      'odin-p-4 odin-border odin-rounded-md',
      hasError
        ? 'odin-border-2 odin-border-odin-danger'
        : 'odin-border-gray-200',
    ),
  error: 'odin-mt-2 odin-text-sm odin-text-odin-danger',
};

export const VideoAcknowledgmentStatus = React.forwardRef(
  (
    props: VideoAcknowledgmentStatusProps,
    ref: React.ForwardedRef<HTMLButtonElement>,
  ): React.ReactElement => {
    const {
      name,
      value,
      onChange,
      error,
      jobsiteWorker,
      documentType,
      isVideoRequired,
      acknowledgedBy,
      loading,
    } = props;
    const workerUser = jobsiteWorker?.contractorWorker?.worker?.user;
    const workerName = getUserFullName(workerUser);

    const { currentUser: user } = React.useContext(AuthContext);

    const { watch, setValue, formState } = useFormContext();
    const acknowledgedByUserId = watch(
      `${documentType?.key}-acknowledged-by-user-id`,
    );
    const acknowledgmentDate = watch(
      `${documentType?.key}-acknowledgment-date`,
    );
    const isAcknowledged = value;
    const isToggleHidden = isAcknowledged && !acknowledgedByUserId;

    const {
      value: isAlertVisible,
      setTrue: showAlert,
      setFalse: hideAlert,
    } = useBoolean();

    const isSiteSpecificOrientationCompleted =
      !!jobsiteWorker?.siteSpecificTrainingCompletedAt;

    const isAcknowledgmentStatusDirty =
      !!formState.dirtyFields[`${documentType?.key}-acknowledgment-status`];
    React.useEffect(() => {
      if (loading || !isVideoRequired || !isSiteSpecificOrientationCompleted)
        return;

      if (isAcknowledgmentStatusDirty) {
        showAlert();
      } else {
        hideAlert();
      }
    }, [
      loading,
      isVideoRequired,
      isSiteSpecificOrientationCompleted,
      isAcknowledgmentStatusDirty,
    ]);

    const getAcknowledgeByText = (): string => {
      if (!isAcknowledged)
        return `${workerName} has not acknowledged this video`;

      if (
        acknowledgedByUserId === acknowledgedBy?.userAccountId ||
        acknowledgedByUserId === user.userAccountId
      ) {
        const acknowledgedByUser =
          acknowledgedByUserId === acknowledgedBy?.userAccountId
            ? acknowledgedBy
            : user;
        const acknowledgedByName = getUserFullName(acknowledgedByUser);
        return `Acknowledged by ${acknowledgedByName} for ${workerName} on ${acknowledgmentDate}`;
      }

      return `Acknowledged by ${workerName} on ${acknowledgmentDate}`;
    };

    const renderError = (): React.ReactNode => {
      if (!error) return null;
      if (typeof error === 'string') {
        return <p className={classes.error}>{error}</p>;
      }
      return <div className={classes.error}>{error}</div>;
    };

    return (
      <>
        <div className={classes.container(!!error)}>
          <div className="odin-flex odin-gap-x-2">
            <Badge
              size="lg"
              color={isAcknowledged ? 'green' : 'yellow'}
              text={isAcknowledged ? 'Complete' : 'Incomplete'}
            />
            {isVideoRequired && (
              <Badge size="lg" color="gray" text="Required" />
            )}
          </div>
          <div className="odin-text-[17px] odin-font-semibold">
            {documentType.name}
          </div>
          <div className="odin-text-xs">{getAcknowledgeByText()} </div>
          <div className={cn('odin-mt-4', isToggleHidden && 'odin-hidden')}>
            <Toggle
              ref={ref}
              name={name}
              value={value}
              description={`Acknowledge on behalf of ${workerName} that they watched the entire training video and understands its contents.`}
              onChange={(checked) => {
                onChange(checked);
                setValue(
                  `${documentType?.key}-acknowledged-by-user-id`,
                  checked ? user.userAccountId : '',
                );
                setValue(
                  `${documentType?.key}-acknowledgment-date`,
                  checked ? getPrettyFormattedDate(moment()) : '',
                );
              }}
            />
          </div>
          {isAlertVisible && (
            <Alert
              type="danger"
              text={
                'The worker has already completed their site-specific orientation. ' +
                'Updating their video acknowledgement will not change their site access or onboarding status.'
              }
            />
          )}
        </div>
        {renderError()}
      </>
    );
  },
);
VideoAcknowledgmentStatus.displayName = 'VideoAcknowledgmentStatus';
